import { Client } from "colyseus.js";
import type { Direction, SnakeState } from "../types/state";

export interface NetworkEvents {
  onConnectChange(online: boolean, label: string): void;
  onAssign(playerIndex: number): void;
  onState(state: SnakeState): void;
  onNotice(message: string): void;
  onError(message: string): void;
}

interface ServerAssignMessage {
  player: number;
}

interface ColyseusRoom {
  send(type: string, message?: unknown): void;
  leave(): Promise<unknown> | void;
  onMessage(type: string, callback: (message: unknown) => void): void;
  onStateChange(callback: (state: unknown) => void): void;
  onLeave(callback: () => void): void;
}

interface ServerTextMessage {
  message: string;
}

export class NetworkClient {
  private readonly events: NetworkEvents;
  private readonly token: string;
  private room: ColyseusRoom | null = null;
  private reconnectTimer: number | null = null;
  private manuallyClosed = false;
  private roomCode = "";

  public constructor(events: NetworkEvents) {
    this.events = events;
    this.token = this.loadToken();
  }

  public async connect(roomCode: string): Promise<void> {
    const cleanRoomCode = this.sanitizeRoomCode(roomCode);
    if (!cleanRoomCode) {
      this.events.onError("Código de sala inválido.");
      return;
    }

    if (this.room && this.roomCode === cleanRoomCode) return;
    this.disconnect(false);
    this.roomCode = cleanRoomCode;
    this.manuallyClosed = false;
    this.events.onConnectChange(false, "conectando...");

    try {
      const client = new Client(this.endpoint());
      const room = await client.joinOrCreate("snake", {
        token: this.token,
        roomCode: cleanRoomCode
      });
      const typedRoom = room as ColyseusRoom;
      this.room = typedRoom;
      this.events.onConnectChange(true, "online");

      typedRoom.onMessage("assign", (message: unknown) => {
        const payload = message as ServerAssignMessage;
        this.events.onAssign(Number(payload.player));
      });

      typedRoom.onMessage("notice", (message: unknown) => {
        const payload = message as ServerTextMessage;
        this.events.onNotice(String(payload.message || ""));
      });

      typedRoom.onMessage("error", (message: unknown) => {
        const payload = message as ServerTextMessage;
        this.events.onError(String(payload.message || ""));
      });

      typedRoom.onStateChange((state: unknown) => {
        this.events.onState(this.normalizeState(state));
      });

      typedRoom.onLeave(() => {
        this.room = null;
        this.events.onConnectChange(false, "reconectando...");
        if (!this.manuallyClosed) this.scheduleReconnect();
      });
    } catch (error) {
      this.room = null;
      this.events.onConnectChange(false, "erro");
      console.error("Falha ao conectar ao Colyseus:", error);
      const message = error instanceof Error ? error.message : String(error);
      this.events.onError(`Não foi possível conectar ao servidor. ${message} Endpoint: ${this.endpoint()}`);
      this.scheduleReconnect();
    }
  }

  public disconnect(markManual = true): void {
    this.manuallyClosed = markManual;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const room = this.room;
    this.room = null;
    void room?.leave();
  }

  public join(name: string, colorId: number): void {
    this.room?.send("join", { token: this.token, name, colorId });
  }

  public leaveGame(): void {
    this.room?.send("leave", {});
  }

  public setDirection(direction: Direction): void {
    this.room?.send("direction", { direction });
  }

  public setSpeed(level: number): void {
    this.room?.send("speed", { level });
  }

  public setWalls(enabled: boolean): void {
    this.room?.send("walls", { enabled });
  }

  public start(): void {
    this.room?.send("start", {});
  }

  public restart(): void {
    this.room?.send("restart", {});
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || !this.roomCode) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect(this.roomCode);
    }, 900);
  }

  private endpoint(): string {
    const configured = import.meta.env.VITE_COLYSEUS_URL as string | undefined;
    if (configured && configured.trim().length > 0) return configured.trim().replace(/\/$/, "");

    const isViteDevServer = window.location.port === "5173";
    if (isViteDevServer) {
      return `${window.location.protocol}//${window.location.hostname}:2567`;
    }

    // Produção oficial deste pacote: https://cesd.roazevedo.com/snakecesd/
    // Mantemos o caminho explícito para evitar cliente antigo tentando /colyseus
    // ou endpoint raiz sem o prefixo do nginx.
    return `${window.location.origin}/snakecesd`;
  }

  private loadToken(): string {
    try {
      const saved = window.localStorage.getItem("cesd-snake-token");
      if (saved && /^[A-Za-z0-9]{8,40}$/.test(saved)) return saved;
      const next = `${Date.now()}${Math.floor(Math.random() * 1_000_000_000)}`;
      window.localStorage.setItem("cesd-snake-token", next);
      return next;
    } catch {
      return `${Date.now()}${Math.floor(Math.random() * 1_000_000_000)}`;
    }
  }

  private sanitizeRoomCode(value: string): string {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  }

  private normalizeState(raw: unknown): SnakeState {
    const state = raw as Record<string, unknown>;
    return {
      phase: String(state.phase || "lobby") as SnakeState["phase"],
      roomCode: String(state.roomCode || this.roomCode),
      winner: Number(state.winner ?? -1),
      host: Number(state.host ?? -1),
      roundPlayers: Number(state.roundPlayers ?? 0),
      speedLevel: Number(state.speedLevel ?? 1),
      tickMs: Number(state.tickMs ?? 160),
      walls: Boolean(state.walls ?? true),
      gridW: Number(state.gridW ?? 36),
      gridH: Number(state.gridH ?? 21),
      version: String(state.version || "pixi-colyseus"),
      players: Array.from((state.players as Iterable<unknown>) || []).map((p) => {
        const player = p as Record<string, unknown>;
        return {
          i: Number(player.i ?? 0),
          joined: Boolean(player.joined),
          connected: Boolean(player.connected),
          alive: Boolean(player.alive),
          name: String(player.name || ""),
          death: String(player.death || ""),
          colorId: Number(player.colorId ?? 0),
          hex: String(player.hex || "#CDD6F4"),
          direction: String(player.direction || "right") as SnakeState["players"][number]["direction"],
          len: Number(player.len ?? 0),
          body: Array.from((player.body as Iterable<unknown>) || []).map((point) => {
            const pt = point as Record<string, unknown>;
            return { x: Number(pt.x ?? 0), y: Number(pt.y ?? 0) };
          })
        };
      }),
      foods: Array.from((state.foods as Iterable<unknown>) || []).map((point) => {
        const pt = point as Record<string, unknown>;
        return { x: Number(pt.x ?? 0), y: Number(pt.y ?? 0) };
      }),
      colors: Array.from((state.colors as Iterable<unknown>) || []).map((color) => {
        const c = color as Record<string, unknown>;
        return {
          id: Number(c.id ?? 0),
          name: String(c.name || "COR"),
          hex: String(c.hex || "#CDD6F4"),
          taken: Boolean(c.taken)
        };
      })
    };
  }
}
